import {
  AfterContentInit, ChangeDetectionStrategy, ChangeDetectorRef, Component,
  ContentChild, EventEmitter, HostListener, Input, OnChanges,
  Output, SimpleChanges, TemplateRef, ViewEncapsulation
} from '@angular/core';
import { Sort } from '@angular/material/sort';
import { UserEvent } from 'src/app/model/user.event';
import {
  PeopleTableBatchActionsDirective, PeopleTableFooterAddDirective,
  PeopleTableRowActionsDirective,
} from './people-table.directives';

/**
 * Identificadores das 5 colunas padrão que o componente conhece + permite
 * strings arbitrárias para colunas extras configuradas via `[extraColumns]`.
 * A ORDEM em `[columns]` controla como aparecem no header/body — position é
 * sempre primeiro, actions+select sempre últimos.
 */
export type PeopleTableColumn = 'name' | 'affiliation' | 'country' | 'email' | 'invite-date' | string;

export const DEFAULT_PEOPLE_TABLE_COLUMNS: PeopleTableColumn[] = [
  'name', 'affiliation', 'country', 'email', 'invite-date',
];

/**
 * Configuração de coluna EXTRA fornecida pelo consumidor. O `id` deve
 * aparecer em `[columns]` na ordem desejada; `cellTemplate` projeta o
 * conteúdo de cada `<td>` (recebe a row via `let-row`); opcionalmente
 * `sortable: true` ativa o `mat-sort-header`. Implementar isso aqui (no
 * lugar de aceitar `<ng-container matColumnDef>` projetado) é necessário
 * porque o `mat-sort-header` precisa do `MatSort` via DI — só funciona
 * se a definição da coluna viver dentro do template do people-table.
 */
export interface PeopleTableExtraColumn {
  id: string;
  label: string;
  sortable?: boolean;
  cellTemplate: TemplateRef<{ $implicit: UserEvent, row: UserEvent }>;
}

/**
 * Tabela reutilizável de pessoas (TPC, chairs, etc.). Encapsula:
 *   • Chrome `.jems3-table` + `.jems3-table-selectable` + numbering
 *   • Hover/selected via pseudo-element ::after (técnica do v2)
 *   • Sort persistido em localStorage por (eventId × persistKey)
 *   • Footer-row "adicionar" (opcional, via template projetado)
 *   • Barra de ações flutuante (opcional, via template projetado) — fica
 *     fora do <table>, como sibling, igual ao Checklist / submissions list
 *   • Seleção por checkbox + master toggle no header
 *   • Copy HTML inline pro clipboard (CTRL+C + botão emitido via output)
 *   • Click "fora" desmarca tudo
 *
 * O consumidor projeta colunas extras via `<ng-container matColumnDef="...">`
 * e templates pra footer-add / row-actions / batch-actions.
 */
@Component({
  selector: 'app-people-table',
  templateUrl: './people-table.component.html',
  styleUrls: ['./people-table.component.scss'],
  encapsulation: ViewEncapsulation.None,
  // OnPush: consumidores (event-members, event-chairs) sempre atribuem NOVA
  // referência a `dataSource` em filtro/sort (ver `filteredMembers = ...` /
  // `[...filtered].sort()` em event-members). Mutações locais de seleção
  // (`row.selected`) rodam via event bindings do template (mat-checkbox
  // `(ngModelChange)` / `(change)`) — Angular marca o próprio componente
  // pra CD nesses casos. `document:click`/`document:copy` HostListeners
  // também disparam CD do próprio componente. Ganho: 4× 351 linhas com
  // mat-tooltip/mat-checkbox/pipes deixam de ser reconciliadas em cada
  // tick de zone alheio (chair-tabs resize, XHR distante, etc).
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PeopleTableComponent implements OnChanges, AfterContentInit {
  /** Dados da tabela. */
  @Input() dataSource: Array<UserEvent> = [];

  /** Lista ordenada das colunas que devem aparecer entre `position` e
   * `actions`/`select`. Pode conter as 5 padrão (com cell templates internos)
   * e/ou ids declarados em `[extraColumns]`. Default = 5 padrão. */
  @Input() columns: PeopleTableColumn[] = DEFAULT_PEOPLE_TABLE_COLUMNS.slice();

  /** Colunas extras (ids, labels, opcionalmente sortable, cellTemplate). O
   * componente renderiza header + cell internamente — necessário pra que o
   * `mat-sort-header` enxergue o `MatSort` via DI. */
  @Input() extraColumns: PeopleTableExtraColumn[] = [];

  /** Override do template do `<td>` da coluna `name`. Quando fornecido,
   * substitui o link padrão — útil pra adicionar badges, botões inline ou
   * estilização condicional (ex: nome em vermelho quando user inativo). */
  @Input() nameCellTemplate?: TemplateRef<{ $implicit: UserEvent, row: UserEvent }>;

  /** ID do evento — chave externa pra persistência de sort. */
  @Input() eventId: number | string | null = null;

  /** Sufixo da chave de localStorage do sort, junto com eventId. Ex.: `'invited'`. */
  @Input() sortPersistKey = '';

  /** Sort inicial caso não haja nada persistido. */
  @Input() defaultSort: Sort = { active: 'name', direction: 'asc' };

  /** Mostra coluna de checkbox + master (necessário pra batch actions). */
  @Input() selectable = true;

  /** Mostra coluna de Ações (depende do template appPeopleTableRowActions). */
  @Input() showRowActions = true;

  /** Aria-label da tabela. */
  @Input() ariaLabel = '';

  /** Modo de decolagem da barra flutuante de ações em lote. Default
   * `at_least` = decola sempre que sair de vista, já que a barra projeta
   * pelo menos 1 botão sempre ativo (Importar, Baixar quando lista tem
   * itens, etc.). Para limitar a decolagem APENAS quando há seleção, passe
   * explicitamente `[floatingMode]="'conservative'"`. Veja
   * `FloatingSubmitBarComponent.floatingMode` para detalhes dos modos. */
  @Input() floatingMode: 'always' | 'at_least' | 'conservative' | 'never' = 'at_least';

  /** Emite a lista de linhas selecionadas quando o estado muda. */
  @Output() selectionChange = new EventEmitter<Array<UserEvent>>();

  /** Emite o estado do sort após mudança (já persistido). */
  @Output() sortChange = new EventEmitter<Sort>();

  /** Emite quando o usuário pede pra copiar (via CTRL+C ou botão na barra). */
  @Output() copyRequested = new EventEmitter<Array<UserEvent>>();

  @ContentChild(PeopleTableFooterAddDirective) footerAdd?: PeopleTableFooterAddDirective;
  @ContentChild(PeopleTableRowActionsDirective) rowActions?: PeopleTableRowActionsDirective;
  @ContentChild(PeopleTableBatchActionsDirective) batchActions?: PeopleTableBatchActionsDirective;

  /** Estado de sort atual. */
  sortState: Sort = { active: 'name', direction: 'asc' };

  /** Lista derivada de colunas pra `*matHeaderRowDef` / `*matRowDef`. */
  displayedColumns: string[] = [];

  /** Última instância da tabela (pra capturar matColumnDef projetados). */
  // @ViewChild(MatTable) private matTable?: MatTable<UserEvent>;

  private static readonly SORT_STORAGE_KEY = 'jems3-people-table-sort-by-event';

  /**
   * trackBy p/ `*matRowDef`. Sem isso, o mat-table re-cria todo o DOM da linha
   * quando a referência do array de dados muda (o consumidor troca dataSource
   * a cada filtro/sort). Com `user.id` como identidade, apenas linhas que
   * entram/saem são criadas/removidas — reordenação move nós existentes.
   * Ganho grande em listas com muitos sub-componentes por linha
   * (mat-tooltip, mat-checkbox, app-select-input-group, etc.).
   */
  trackByUserId = (_: number, row: UserEvent): any => row?.user?.id ?? row;

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['eventId'] || changes['sortPersistKey']) {
      this.restoreSort();
      // Emite sortChange após restaurar pra que o consumidor reaplique o sort
      // ao seu dataSource (a tabela só mostra o indicador; ordenar é
      // responsabilidade do consumidor — vide (sortChange) no template).
      this.sortChange.emit(this.sortState);
    }
    if (changes['columns'] || changes['selectable'] || changes['showRowActions']) {
      this.rebuildDisplayedColumns();
    }
    // Nova referência do array (o consumidor reatribui em filtro/sort) →
    // pode ter mudado o conjunto de linhas com `selected: true`. Recomputa
    // o estado derivado da seleção O(N), única vez por atribuição, em vez
    // de re-iterar por CD.
    if (changes['dataSource']) {
      this.recomputeSelection();
    }
  }

  ngAfterContentInit(): void {
    this.rebuildDisplayedColumns();
  }

  // ─── Sort ────────────────────────────────────────────────────────────

  onSortChange(sort: Sort): void {
    this.sortState = sort;
    this.persistSort();
    this.sortChange.emit(sort);
  }

  private persistSort(): void {
    if (!this.eventId || !this.sortPersistKey) { return; }
    try {
      const raw = localStorage.getItem(PeopleTableComponent.SORT_STORAGE_KEY);
      const map = raw ? JSON.parse(raw) : {};
      const k = `${this.eventId}:${this.sortPersistKey}`;
      map[k] = this.sortState;
      localStorage.setItem(PeopleTableComponent.SORT_STORAGE_KEY, JSON.stringify(map));
    } catch { /* localStorage indisponível */ }
  }

  private restoreSort(): void {
    this.sortState = { ...this.defaultSort };
    if (!this.eventId || !this.sortPersistKey) { return; }
    try {
      const raw = localStorage.getItem(PeopleTableComponent.SORT_STORAGE_KEY);
      if (!raw) { return; }
      const map = JSON.parse(raw);
      const k = `${this.eventId}:${this.sortPersistKey}`;
      const saved = map?.[k];
      if (saved?.active && saved?.direction) {
        this.sortState = saved as Sort;
      }
    } catch { /* malformado */ }
  }

  // ─── Seleção ──────────────────────────────────────────────────────────
  //
  // Estado derivado da seleção mantido em CAMPOS (não getters). Antes cada
  // um era chamado no template a cada CD, e ambos iteravam `dataSource`
  // (351 linhas). O template consumia `selectedRows()` **duas vezes** no
  // contexto do floating-submit-bar → duas passadas de `.filter` por CD.
  //
  // Agora atualizamos os campos quando algo REALMENTE muda a seleção:
  //   • ngOnChanges no `dataSource` (nova referência de array vinda do pai)
  //   • toggleAll (checkbox master)
  //   • onRowSelectionChange (checkbox de linha via `(ngModelChange)`)
  // O template lê propriedades — O(1) por CD.

  _selectedRows: Array<UserEvent> = [];
  _hasSelection = false;
  _allSelected = false;
  _someSelected = false;

  hasSelection(): boolean { return this._hasSelection; }
  selectedRows(): Array<UserEvent> { return this._selectedRows; }
  allSelected(): boolean { return this._allSelected; }
  someSelected(): boolean { return this._someSelected; }

  private recomputeSelection(): void {
    const rows = this.dataSource || [];
    const selected: Array<UserEvent> = [];
    for (const r of rows) {
      if ((r as any).selected) { selected.push(r); }
    }
    this._selectedRows = selected;
    this._hasSelection = selected.length > 0;
    this._allSelected = rows.length > 0 && selected.length === rows.length;
    this._someSelected = this._hasSelection && !this._allSelected;
  }

  toggleAll(checked: boolean): void {
    (this.dataSource || []).forEach(r => { (r as any).selected = checked; });
    this.recomputeSelection();
    this.emitSelection();
  }

  onRowSelectionChange(): void {
    this.recomputeSelection();
    this.emitSelection();
  }

  private emitSelection(): void {
    this.selectionChange.emit(this._selectedRows);
  }

  // ─── Click "fora" desmarca tudo ──────────────────────────────────────

  @HostListener('document:click', ['$event'])
  onDocumentClickClearSelection(event: MouseEvent): void {
    if (!this.hasSelection()) { return; }
    const target = event.target as HTMLElement;
    if (!target || typeof target.closest !== 'function') { return; }
    const selector = 'button, a, input, textarea, select, label, [role="button"], [role="menuitem"], [role="option"], mat-checkbox, mat-menu-item, mat-option, mat-form-field, app-user-search';
    if (target.closest(selector)) { return; }
    this.toggleAll(false);
  }

  // ─── CTRL+C / ⌘C ─────────────────────────────────────────────────────

  @HostListener('document:copy', ['$event'])
  onDocumentCopy(event: ClipboardEvent): void {
    if (!this.hasSelection()) { return; }
    const ae = document.activeElement as HTMLInputElement | null;
    const nonText = ['checkbox', 'radio', 'button', 'submit', 'range', 'color', 'file'];
    const isTextField = !!ae && (
      ae.tagName === 'TEXTAREA' ||
      (ae as HTMLElement).isContentEditable ||
      (ae.tagName === 'INPUT' && !nonText.includes((ae.type || '').toLowerCase()))
    );
    if (isTextField) { return; }
    if ((window.getSelection()?.toString() || '').trim().length) { return; }
    event.preventDefault();
    this.copyRequested.emit(this.selectedRows());
  }

  requestCopy(): void {
    this.copyRequested.emit(this.selectedRows());
  }

  // ─── Layout helpers ──────────────────────────────────────────────────

  private rebuildDisplayedColumns(): void {
    // position é sempre primeiro; consumidor define a ordem das demais em
    // `[columns]`; actions/select vêm no fim. Colunas que o consumidor lista
    // mas o template não conhece (rejected-date, accepted-date, etc.) só
    // funcionam se projetadas via <ng-container matColumnDef> — captadas
    // por ContentChildren e registradas no mat-table em ngAfterContentInit.
    const cols: string[] = ['position', ...(this.columns || [])];
    if (this.showRowActions && this.rowActions) { cols.push('actions'); }
    if (this.selectable) { cols.push('select'); }
    this.displayedColumns = cols;
  }

}
